// ============================================================================
// Config Manager Classes - Provides reactive nested config management
// ============================================================================

import { action, computed, makeObservable, observable } from 'mobx';

import type { IFormConfig } from '../forms';
import type { IDonationFormConfig } from './donation_forms';
import type { IAmountOption, IValidationRule } from './donation_forms.types';

/**
 * Observable wrapper for donation amount options.
 * Provides reactivity and unique ID management for array items.
 */
export class ConfigFieldOption {
  public readonly id: string;
  @observable public description: string;
  @observable public value: string;
  @observable private _dirty = false;
  private _shadowState: { description: string; value: string };
  private _deleteHandler?: () => void;

  constructor(data: IAmountOption & { id?: string }) {
    this.id = data.id ?? crypto.randomUUID();
    this.description = data.description;
    this.value = data.value;
    this._shadowState = {
      description: data.description,
      value: data.value,
    };

    makeObservable(this);
  }

  @computed
  public get isDirty(): boolean {
    return (
      this._dirty ||
      this.description !== this._shadowState.description ||
      this.value !== this._shadowState.value
    );
  }

  @action
  public set(updates: Partial<Omit<IAmountOption, 'id'>>): void {
    if (updates.description !== undefined) {
      this.description = updates.description;
    }
    if (updates.value !== undefined) {
      this.value = updates.value;
    }
    this._dirty = true;
  }

  /**
   * Delete this option from its parent field.
   * Using arrow function to preserve 'this' binding when passed as callback.
   */
  public delete = (): void => {
    if (this._deleteHandler) {
      this._deleteHandler();
    }
  };

  /**
   * Internal method to set the delete handler.
   * Called by ConfigField when the option is created.
   */
  public _setDeleteHandler(handler: () => void): void {
    this._deleteHandler = handler;
  }

  @action
  public resetDirty(): void {
    this._shadowState = {
      description: this.description,
      value: this.value,
    };
    this._dirty = false;
  }

  public toJSON(includeDescription = true): IAmountOption {
    return {
      description: includeDescription ? this.description : '',
      value: this.value,
    };
  }
}

/**
 * Observable wrapper for form field configuration.
 * Manages nested properties and validation rules with reactivity.
 */
export class ConfigField {
  public readonly name: string;
  @observable public properties: Record<string, unknown>;
  @observable public validation: IValidationRule[];
  @observable public options: ConfigFieldOption[];
  @observable private _dirty = false;

  constructor(name: string, data: Record<string, unknown>) {
    this.name = name;
    this.properties = {
      ...((data as { properties?: Record<string, unknown> }).properties ?? {}),
    };
    this.validation = [...((data as { validation?: IValidationRule[] }).validation ?? [])];

    // Special handling for donation-amount field options
    const rawOptions = (this.properties.options as IAmountOption[]) || [];
    this.options = rawOptions.map((opt) => this._createOption(opt));

    makeObservable(this);
  }

  /**
   * Internal method to create an option with delete handler injected
   */
  private _createOption(option: IAmountOption & { id?: string }): ConfigFieldOption {
    const newOption = new ConfigFieldOption(option);
    newOption._setDeleteHandler(() => this.deleteOption(newOption.id));
    return newOption;
  }

  @computed
  public get isDirty(): boolean {
    return this._dirty || this.options.some((opt) => opt.isDirty);
  }

  @action
  public addOption(option: IAmountOption & { id?: string }): ConfigFieldOption {
    const newOption = this._createOption(option);
    this.options.push(newOption);
    this._dirty = true;
    return newOption;
  }

  @action
  public deleteOption(id: string): void {
    const index = this.options.findIndex((opt) => opt.id === id);
    if (index !== -1) {
      this.options.splice(index, 1);
      this._dirty = true;
    }
  }

  @action
  public setValidation(validation: IValidationRule[]): void {
    this.validation = [...validation];
    this._dirty = true;
  }

  @action
  public addValidation(rule: IValidationRule): void {
    this.validation.push(rule);
    this._dirty = true;
  }

  @action
  public removeValidationByType(type: string): void {
    this.validation = this.validation.filter((rule) => rule.type !== type);
    this._dirty = true;
  }

  @action
  public resetDirty(): void {
    this._dirty = false;
    this.options.forEach((opt) => opt.resetDirty());
  }

  public toJSON(includeDescription = true): Record<string, unknown> {
    return {
      properties: {
        ...this.properties,
        options: this.options.map((opt) => opt.toJSON(includeDescription)),
      },
      validation: this.validation,
    };
  }
}

/**
 * Main config manager for DonationForm.
 * Provides reactive nested attribute management with granular dirty tracking.
 */
export class DonationFormConfigManager {
  @observable public fields: Map<string, ConfigField>;
  @observable public settings: IFormConfig['settings'];
  @observable public accountId: string;
  @observable public allowMonthlyDonations: boolean;
  @observable public allowDescriptions: boolean;
  private _shadowState: {
    allowMonthlyDonations: boolean;
    allowDescriptions: boolean;
  };

  constructor(config: IDonationFormConfig) {
    this.accountId = config.account_id;
    this.settings = { ...config.settings };
    this.allowMonthlyDonations = config.allowMonthlyDonations ?? false;
    this.allowDescriptions = config.allowDescriptions ?? false;
    this._shadowState = {
      allowMonthlyDonations: this.allowMonthlyDonations,
      allowDescriptions: this.allowDescriptions,
    };
    this.fields = new Map();

    // Initialize fields
    Object.entries(config.fields || {}).forEach(([fieldName, fieldData]) => {
      this.fields.set(fieldName, new ConfigField(fieldName, fieldData as Record<string, unknown>));
    });

    makeObservable(this);
  }

  @computed
  public get isDirty(): boolean {
    const fieldsAreDirty = Array.from(this.fields.values()).some((field) => field.isDirty);
    const togglesAreDirty =
      this.allowMonthlyDonations !== this._shadowState.allowMonthlyDonations ||
      this.allowDescriptions !== this._shadowState.allowDescriptions;
    return fieldsAreDirty || togglesAreDirty;
  }

  @computed
  public get dirtyFieldNames(): string[] {
    return Array.from(this.fields.values())
      .filter((field) => field.isDirty)
      .map((field) => field.name);
  }

  @action
  public getField(name: string): ConfigField | undefined {
    return this.fields.get(name);
  }

  @action
  public getOrCreateField(name: string): ConfigField {
    let field = this.fields.get(name);
    if (!field) {
      field = new ConfigField(name, {});
      this.fields.set(name, field);
    }
    return field;
  }

  @action
  public resetDirty(): void {
    this.fields.forEach((field) => field.resetDirty());
    this._shadowState = {
      allowMonthlyDonations: this.allowMonthlyDonations,
      allowDescriptions: this.allowDescriptions,
    };
  }

  public toJSON(): IDonationFormConfig {
    const fieldsObject: Record<string, Record<string, unknown>> = {};
    this.fields.forEach((field, name) => {
      // Skip fields with invalid names (undefined, null, empty string)
      if (!name || name === 'undefined' || name === 'null') {
        return;
      }

      // Skip recurring-donation-amount field if monthly donations are disabled
      if (name === 'recurring-donation-amount' && !this.allowMonthlyDonations) {
        return;
      }

      // Pass allowDescriptions flag to field serialization
      fieldsObject[name] = field.toJSON(this.allowDescriptions);
    });

    const allowMonthlyDonationsSettings = {
      ...this.settings,
      recurring: this.allowMonthlyDonations,
    };

    return {
      account_id: this.accountId,
      settings: allowMonthlyDonationsSettings,
      fields: fieldsObject,
      allowMonthlyDonations: this.allowMonthlyDonations,
      allowDescriptions: this.allowDescriptions,
    };
  }
}
